import React, { useState, useMemo, useCallback, useEffect } from "react";
import { DDNav } from "../../../components/Admin/DDNav/DDNav";
import { DDNextBar } from "../../../components/Admin/DDNextBar";
import { DDLoadError } from "../../../components/Admin/DDLoadError";
import { ConfirmModal } from "../../../components/Admin/ConfirmModal/ConfirmModal";
import { DDSiteManager } from "../../../components/Admin/DDSiteManager/DDSiteManager";
import { useAuth, useDdSites } from "../../../hooks";
import { DdOcdeRr } from "../../../api";
import {
  RISKS, RR_DEFAULT, buildDefaultScoringForSite,
  calcSev, calcSig, classify, CLASS_LABEL, CLASS_PILL, PILLARS, pillarOf,
  FACTORS_VERSION, migrateRrData,
} from "./ocdeRrCatalog";
import "./OcdeRiskRegister.scss";

const ddRrApi = new DdOcdeRr();

function persist(token, s) {
  return ddRrApi.saveData(token, { scoring: s.scoring, status: s.status, curIdx: s.curIdx, factorsVersion: FACTORS_VERSION })
    .catch((err) => console.error("Error guardando datos de Risk Register", err));
}

function defaultState() {
  return {
    scoring: JSON.parse(JSON.stringify(RR_DEFAULT)),
    status:  Object.fromEntries(RISKS.map((r) => [r.id, "pending"])),
    curIdx:  0,
  };
}

/** Devuelve una copia de `scoring` con una entrada por defecto para cualquier
 * sitio de `sites` que todavía no tenga datos cargados (agregado desde otro módulo). */
function reconcileScoring(scoring, sites) {
  let changed = false;
  const next = { ...scoring };
  RISKS.forEach((r) => {
    const riskEntry = { ...next[r.id] };
    sites.forEach((s) => {
      if (!riskEntry[s.id]) { riskEntry[s.id] = buildDefaultScoringForSite(r.id); changed = true; }
    });
    next[r.id] = riskEntry;
  });
  return { data: next, changed };
}

const RUBRIC = [
  { crit: "Escala", sub: "gravedad · OCDE 2.1.7",        cols: ["Impacto menor, reversible","Impacto leve sobre derecho fundamental","Impacto moderado sobre dignidad/salud","Impacto grave: daño físico/psicológico serio","Impacto extremo: catastrófico, atenta contra vida"] },
  { crit: "Alcance", sub: "nº afectados · OCDE 2.1.7",   cols: ["≤ 10 personas","11–50 personas","51–200 personas","201–500 personas","> 500 personas"] },
  { crit: "Irremediable", sub: "reversibilidad · OCDE 2.1.7", cols: ["Totalmente reversible","Reversible con esfuerzo menor","Reversible con esfuerzo significativo","Difícilmente reversible","Irremediable · permanente"] },
  { crit: "Probabilidad", sub: "likelihood · OCDE 2.1.7", cols: ["Muy baja · raro","Baja · casos aislados","Media · ocurre periódicamente","Alta · factor documentado","Muy alta · contexto lo favorece"] },
];

export function OcdeRiskRegister() {
  const { accessToken } = useAuth();
  const [state, setState] = useState(defaultState);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);
  const [view, setView]   = useState("form");
  const [methOpen, setMethOpen] = useState(false);
  const [confirm, setConfirm] = useState({ open: false, msg: "", onConfirm: null });
  const { sites, sitesLoading, sitesError, reloadSites, addSite, removeSite } = useDdSites(accessToken);

  // Si la carga falla no se muestra (ni se guarda) el Risk Register de demostración.
  useEffect(() => {
    if (!accessToken) return;
    setLoading(true);
    setLoadError(false);
    ddRrApi.getData(accessToken)
      .then(d => {
        if (!d) return;
        const { data, changed } = migrateRrData(d);
        setState(s => ({ ...s, ...data }));
        if (changed) persist(accessToken, data);
      })
      .catch((err) => { console.error("Error cargando datos de Risk Register", err); setLoadError(true); })
      .finally(() => setLoading(false));
  }, [accessToken, reloadKey]);

  const save = useCallback((next) => {
    setState(next);
    if (accessToken) persist(accessToken, next);
  }, [accessToken]);

  const risk   = RISKS[state.curIdx];
  const scoring = state.scoring;

  // Vista siempre consistente: rellena con valores por defecto cualquier sitio
  // que aún no tenga scoring cargado (recién agregado en este módulo u otro).
  const safeScoring = useMemo(() => reconcileScoring(scoring, sites).data, [scoring, sites]);

  // Persiste esos valores por defecto para que la próxima carga ya los traiga completos.
  useEffect(() => {
    if (loading || sitesLoading || loadError || sitesError) return;
    const { data, changed } = reconcileScoring(state.scoring, sites);
    if (changed) {
      const next = { ...state, scoring: data };
      setState(next);
      if (accessToken) persist(accessToken, next);
    }
  }, [sites, loading, sitesLoading, loadError, sitesError, state, accessToken]);

  function selectRisk(i) {
    const next = { ...state, curIdx: i };
    if ((next.status[RISKS[i].id] || "pending") === "pending") {
      next.status = { ...next.status, [RISKS[i].id]: "progress" };
    }
    save(next);
  }

  function updateScore(rid, sid, key, val) {
    const sc = JSON.parse(JSON.stringify(reconcileScoring(scoring, sites).data));
    sc[rid][sid][key] = parseInt(val, 10);
    save({ ...state, scoring: sc });
  }

  function updateNotes(rid, sid, val) {
    const sc = JSON.parse(JSON.stringify(reconcileScoring(scoring, sites).data));
    sc[rid][sid].notes = val;
    save({ ...state, scoring: sc });
  }

  function toggleFactor(rid, fi, checked) {
    const sc = JSON.parse(JSON.stringify(reconcileScoring(scoring, sites).data));
    sites.forEach((st) => {
      const arr = [...(sc[rid][st.id].factorsChecked || [])];
      const idx = arr.indexOf(fi);
      if (checked && idx === -1) arr.push(fi);
      else if (!checked && idx !== -1) arr.splice(idx, 1);
      sc[rid][st.id].factorsChecked = arr;
    });
    save({ ...state, scoring: sc });
  }

  function toggleComplete() {
    const st = { ...state.status };
    st[risk.id] = st[risk.id] === "done" ? "progress" : "done";
    save({ ...state, status: st });
  }

  function resetAll() {
    setConfirm({
      open: true,
      msg: "¿Resetear el Risk Register a los valores de demostración?",
      onConfirm: () => {
        const fresh = reconcileScoring(JSON.parse(JSON.stringify(RR_DEFAULT)), sites).data;
        save({ scoring: fresh, status: Object.fromEntries(RISKS.map((r) => [r.id, "pending"])), curIdx: 0 });
        setConfirm({ open: false, msg: "", onConfirm: null });
      },
    });
  }

  const exportData = () => {
    const blob = new Blob([JSON.stringify({ scoring: safeScoring, status: state.status }, null, 2)], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "sustemia-rr-" + new Date().toISOString().slice(0, 10) + ".json";
    a.click();
    URL.revokeObjectURL(url);
  };

  const doneCount = Object.values(state.status).filter((s) => s === "done").length;
  const riskStatus = state.status[risk.id] || "pending";

  /* ---------- summary data ---------- */
  const summaryData = useMemo(() => {
    const counts = { low: 0, med: 0, high: 0, crit: 0 };
    const rows = RISKS.map((r) => {
      const sigs = sites.map((st) => calcSig(safeScoring[r.id][st.id]));
      const avg  = sigs.reduce((a, b) => a + b, 0) / sigs.length;
      counts[classify(avg)]++;
      return { risk: r, sigs, avg };
    });
    return { rows, counts };
  }, [safeScoring, sites]);

  /* ---------- saliency matrix ---------- */
  const saliencyItems = useMemo(() =>
    summaryData.rows.map((d) => {
      const avgSev  = sites.reduce((a, st) => a + calcSev(safeScoring[d.risk.id][st.id]), 0) / sites.length;
      const avgProb = sites.reduce((a, st) => a + safeScoring[d.risk.id][st.id].likelihood, 0) / sites.length;
      return { mod: d.risk.mod, name: d.risk.name, idx: RISKS.indexOf(d.risk), roundSev: Math.round(avgSev), roundProb: Math.round(avgProb) };
    }),
  [safeScoring, sites, summaryData]);

  if (loading || sitesLoading) return <div className="rr-page"><DDNav /><div style={{ padding: "40px 22px", color: "#6B7280" }}>Cargando…</div></div>;
  if (loadError || sitesError) {
    return <DDLoadError className="rr-page" onRetry={() => { if (loadError) setReloadKey((k) => k + 1); if (sitesError) reloadSites(); }} />;
  }

  return (
    <div className="rr-page">
      <DDNav />

      <div className="rr-ph">
        <div>
          <h1 className="rr-title">Risk Register OCDE</h1>
          <span className="rr-subtitle">DD · Etapa 2.1 · Ejercicio de delimitación de riesgos · pilares Social, SST y Ambiente · Guía OCDE 2021 Sección 2</span>
        </div>
        <div className="rr-ph-r">
          <button className="rr-btn-export" onClick={exportData}>⇩ Exportar JSON</button>
          <button className="rr-btn-reset" onClick={resetAll}>↻ Reset</button>
        </div>
      </div>

      <div className="rr-viewsw">
        <button className={view === "form" ? "active" : ""} onClick={() => setView("form")}>📋 Cargar expediente</button>
        <button className={view === "summary" ? "active" : ""} onClick={() => setView("summary")}>📊 Resumen del registro</button>
      </div>

      <DDSiteManager
        sites={sites}
        onAdd={addSite}
        onRemove={removeSite}
      />

      <div className={`rr-meth-panel${methOpen ? " rr-meth-panel--open" : ""}`}>
        <div className="rr-mp-head" onClick={() => setMethOpen((o) => !o)}>
          <h3>📖 Metodología · cómo usar este Risk Register según OCDE 2021</h3>
          <span className="rr-mp-toggle">{methOpen ? "▲" : "▼"}</span>
        </div>
        {methOpen && (
          <div className="rr-mp-body">
            <h5>📌 Fuente única de verdad</h5>
            <p>Implementa la <strong>Sección 2.1 (Ejercicio de delimitación de riesgos)</strong> y específicamente la <strong>2.1.7 (Identificar los riesgos más significativos)</strong> de la <em>Guía OCDE 2021</em> (ISBN 978-92-64-45119-3).</p>
            <h5>🧭 Lo que dice OCDE textualmente</h5>
            <div className="rr-source">
              <p>"Basándose en toda la información conocida, la empresa debe determinar cuáles riesgos de daños son más significativos – con relación a su probabilidad y severidad – en sus propias operaciones y en su cadena de suministro, y priorizar esos riesgos para la acción en primer lugar. La severidad de daño se juzga en función de la escala, alcance y su carácter de irremediable."</p>
              <span className="rr-source-src">— Guía OCDE 2021, Sección 2.1.7</span>
            </div>
            <h5>📐 Cómo OCDE define cada criterio (texto literal)</h5>
            <p><strong>Escala</strong> — "se refiere a la gravedad del impacto negativo." (2.1.7)</p>
            <p><strong>Alcance</strong> — "se refiere al número de individuos que son o serán afectados." (2.1.7)</p>
            <p><strong>Carácter de irremediable</strong> — "cualquier límite en la capacidad de reestablecer a los afectados, al menos, a la misma situación en la que se encontraban previo al impacto negativo o a una equivalente a esta." (2.1.7)</p>
            <p><strong>Probabilidad</strong> — se evalúa basándose en factores sectoriales, productos, país, modelo de negocio y modelo de abastecimiento (Secciones 2.1.2 a 2.1.6).</p>
            <div className="rr-disclaim">
              <strong>Aclaración:</strong> la Guía OCDE define los criterios cualitativamente pero <strong>no establece una escala numérica</strong>. "Determinar los riesgos de daños más significativos involucra cierto grado de juicio por parte de la empresa". La rúbrica 1-5 es operacionalización Sustemia del marco cualitativo OCDE.
            </div>
            <h5>🧮 Fórmula operacional Sustemia</h5>
            <div className="rr-formula">
              <strong>Severidad</strong> = (Escala + Alcance + Irremediable) / 3<br />
              <strong>Significance</strong> = Severidad × Probabilidad<br />
              Bandas: <strong>Bajo</strong> 1–5 · <strong>Medio</strong> 6–10 · <strong>Alto</strong> 11–15 · <strong>Crítico</strong> 16–25
            </div>
          </div>
        )}
      </div>

      {view === "form" ? (
        <RRFormView
          risk={risk}
          risks={RISKS}
          sites={sites}
          scoring={safeScoring}
          status={state.status}
          curIdx={state.curIdx}
          doneCount={doneCount}
          riskStatus={riskStatus}
          onSelectRisk={selectRisk}
          onUpdateScore={updateScore}
          onUpdateNotes={updateNotes}
          onToggleFactor={toggleFactor}
          onToggleComplete={toggleComplete}
        />
      ) : (
        <RRSummaryView
          sites={sites}
          summaryData={summaryData}
          saliencyItems={saliencyItems}
          onGoToRisk={(i) => { selectRisk(i); setView("form"); }}
        />
      )}
      {view === "form" && (
        <DDNextBar
          title="Siguiente · Autoevaluación 2.2"
          text="Evaluá la madurez de tus operaciones propias en las 9 áreas."
          button="Ir a Autoevaluación"
          to="/admin/self-assessments"
        />
      )}
      <ConfirmModal
        open={confirm.open}
        message={confirm.msg}
        danger
        onConfirm={confirm.onConfirm}
        onCancel={() => setConfirm({ open: false, msg: "", onConfirm: null })}
      />
    </div>
  );
}

/* ─────────────────────────── Form view ─────────────────────────── */

function RRFormView({ risk, risks, sites, scoring, status, curIdx, doneCount, riskStatus, onSelectRisk, onUpdateScore, onUpdateNotes, onToggleFactor, onToggleComplete }) {
  const sigs  = sites.map((st) => calcSig(scoring[risk.id][st.id]));
  const avg   = sigs.reduce((a, b) => a + b, 0) / sigs.length;
  const maxIdx = sigs.indexOf(Math.max(...sigs));
  const minIdx = sigs.indexOf(Math.min(...sigs));
  const avgCls = classify(avg);

  const anyFactorChecked = (fi) => sites.some((st) => (scoring[risk.id][st.id].factorsChecked || []).includes(fi));

  const statusIcon = { done: "✓", progress: "◐", pending: "○" };
  const statusLabel = { done: "Completo", progress: "Borrador", pending: "Pendiente" };
  const statusPill  = { done: "g", progress: "y", pending: "muted" };

  return (
    <div className="rr-formwrap">
      {/* Header del riesgo */}
      <div className="rr-formhd">
        <div className="rr-formhd-id">
          <code className="rr-code">{risk.code}</code>
          <span className={`pill ${statusPill[riskStatus]}`}>{statusLabel[riskStatus]}</span>
          <PillarChip risk={risk} />
          <span className="rr-formhd-title">{risk.name}</span>
        </div>
        <div className="rr-formhd-actions">
          <span className="rr-prog-label">{doneCount}/{risks.length} completos</span>
          <button className="rr-btn-nav" disabled={curIdx === 0} onClick={() => onSelectRisk(curIdx - 1)}>← Anterior</button>
          <button className="rr-btn-nav" disabled={curIdx === risks.length - 1} onClick={() => onSelectRisk(curIdx + 1)}>Siguiente →</button>
          <button className="rr-btn-complete" onClick={onToggleComplete}>
            {riskStatus === "done" ? "↩ Reabrir" : "✓ Marcar completo"}
          </button>
        </div>
      </div>

      {/* Tabs horizontales */}
      <div className="rr-tabs">
        {risks.map((r, i) => (
          <button
            key={r.id}
            className={`rr-tab${i === curIdx ? " active" : ""}`}
            onClick={() => onSelectRisk(i)}
          >
            {i + 1} · {r.short}
            <span className="rr-tab-n">{statusIcon[status[r.id]] || "○"}</span>
          </button>
        ))}
      </div>

      {/* Content */}
      <div className="rr-content">
        {/* OCDE quote hint */}
        <div className="rr-hint">
          <span className="rr-hint-ic">✦</span>
          <div>
            <strong>{risk.section}</strong> — {risk.def}
            {risk.quote && <div className="rr-hint-src">"{risk.quote}" — <em>{risk.quoteSrc}</em></div>}
          </div>
        </div>

        {/* Identificación */}
        <div className="rr-fsec">
          <h4>Identificación del riesgo <span className="rr-h-meta">datos del catálogo OCDE</span></h4>
          <div className="rr-fr rr-c4">
            <div className="rr-fld"><label>Código</label><input readOnly value={risk.code} /></div>
            <div className="rr-fld"><label>Riesgo OCDE</label><input readOnly value={risk.name} /></div>
            <div className="rr-fld"><label>Módulo</label><input readOnly value={risk.mod} /></div>
            <div className="rr-fld"><label>Referencia</label><input readOnly value={risk.section} /></div>
          </div>
        </div>

        {/* Factores de riesgo */}
        <div className="rr-fsec">
          <h4>Factores de riesgo identificados por la Guía OCDE <span className="rr-h-meta">marcá los que aplican</span></h4>
          <div className="rr-factor-list">
            {risk.factors.map((f, fi) => (
              <label key={fi} className="rr-factor-item">
                <input
                  type="checkbox"
                  checked={anyFactorChecked(fi)}
                  onChange={(e) => onToggleFactor(risk.id, fi, e.target.checked)}
                />
                {f}
              </label>
            ))}
          </div>
        </div>

        {/* Rúbrica */}
        <div className="rr-rubric">
          <h5>Rúbrica de scoring · cómo asignar 1, 2, 3, 4 o 5</h5>
          <div className="rr-rubric-intro">Los criterios son textuales de OCDE Sección 2.1.7. Los rangos 1-5 son operacionalización Sustemia.</div>
          <table className="rr-rubric-table">
            <thead>
              <tr><th>Criterio</th>{["Mínimo", "Bajo", "Medio", "Alto", "Máximo"].map((lbl, i) => <th key={lbl}><span className={`rr-lvl l${i + 1}`}>{i + 1}</span><span className="rr-lvl-lbl">{lbl}</span></th>)}</tr>
            </thead>
            <tbody>
              {RUBRIC.map((row) => (
                <tr key={row.crit}>
                  <td className="rr-crit">{row.crit}<span className="rr-crit-sub">{row.sub}</span></td>
                  {row.cols.map((c, i) => <td key={i}>{c}</td>)}
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Scoring por sitio */}
        <div className="rr-fsec">
          <h4>Scoring por sitio · OCDE 2.1.7 <span className="rr-h-meta">severidad=(escala+alcance+irremediable)/3 · significance=severidad×probabilidad</span></h4>
          <table className="rr-scoring-table">
            <thead>
              <tr>
                <th>Sitio</th><th>Escala</th><th>Alcance</th><th>Irremediable</th>
                <th>Severidad</th><th>Probabilidad</th><th>Significance</th><th>Nivel</th>
              </tr>
            </thead>
            <tbody>
              {sites.map((st) => {
                const s   = scoring[risk.id][st.id];
                const sev = calcSev(s);
                const sig = calcSig(s);
                const cls = classify(sig);
                return (
                  <React.Fragment key={st.id}>
                    <tr>
                      <td className="rr-site-cell">
                        <span className="flag-pill">{st.flag}</span>{st.name}
                      </td>
                      <td><ScoreSelect rid={risk.id} sid={st.id} fkey="scale"        val={s.scale}        onChange={onUpdateScore} /></td>
                      <td><ScoreSelect rid={risk.id} sid={st.id} fkey="scope"        val={s.scope}        onChange={onUpdateScore} /></td>
                      <td><ScoreSelect rid={risk.id} sid={st.id} fkey="irremediable" val={s.irremediable} onChange={onUpdateScore} /></td>
                      <td><span className="rr-calc">{sev.toFixed(2)}</span></td>
                      <td><ScoreSelect rid={risk.id} sid={st.id} fkey="likelihood"   val={s.likelihood}   onChange={onUpdateScore} /></td>
                      <td><span className={`rr-sig ${cls}`}>{sig.toFixed(2)}</span></td>
                      <td><span className={`pill ${CLASS_PILL[cls]}`}>{CLASS_LABEL[cls]}</span></td>
                    </tr>
                    <tr className="rr-notes-row">
                      <td colSpan={8}>
                        <div className="rr-fld">
                          <label><span className="flag-pill">{st.flag}</span> Justificación contextual · {st.name}</label>
                          <textarea
                            value={s.notes || ""}
                            placeholder="Anclá el scoring en factores OCDE…"
                            onChange={(e) => onUpdateNotes(risk.id, st.id, e.target.value)}
                          />
                        </div>
                      </td>
                    </tr>
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>

        {/* Conclusión grupo */}
        <div className="rr-fsec">
          <h4>Conclusión del riesgo a nivel grupo</h4>
          <div className="rr-concl-grid">
            <div className={`rr-concl-card ${avgCls}`}>
              <div className="lbl">Significance promedio</div>
              <div className="vl">{avg.toFixed(2)}</div>
              <div className="meta">de 25 (OCDE)</div>
            </div>
            <div className="rr-concl-card">
              <div className="lbl">Nivel del grupo</div>
              <div className="vl"><span className={`pill ${CLASS_PILL[avgCls]}`}>{CLASS_LABEL[avgCls]}</span></div>
              <div className="meta">OCDE 2.1.7</div>
            </div>
            <div className="rr-concl-card high">
              <div className="lbl">Sitio con mayor exposición</div>
              <div className="vl sm">{sites[maxIdx].flag} · {sites[maxIdx].name}</div>
              <div className="meta">significance {sigs[maxIdx].toFixed(2)}</div>
            </div>
            <div className="rr-concl-card low">
              <div className="lbl">Sitio con menor exposición</div>
              <div className="vl sm">{sites[minIdx].flag} · {sites[minIdx].name}</div>
              <div className="meta">significance {sigs[minIdx].toFixed(2)}</div>
            </div>
          </div>
          {(avgCls === "crit" || avgCls === "high") && (
            <div className="rr-action-box">
              <div className="ttl">⚠ Riesgo significativo · acción en Etapa 3 (CAP Tracker)</div>
              <p>Califica como <strong>{CLASS_LABEL[avgCls]}</strong> y alimenta el CAP Tracker de la Etapa 3.1.</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function PillarChip({ risk }) {
  const p = PILLARS[pillarOf(risk)];
  return <span className="rr-pillar" style={{ background: p.bg, color: p.fg }}>{p.label}</span>;
}

function ScoreSelect({ rid, sid, fkey, val, onChange }) {
  return (
    <select value={val} onChange={(e) => onChange(rid, sid, fkey, e.target.value)}>
      {[1,2,3,4,5].map((v) => <option key={v} value={v}>{v}</option>)}
    </select>
  );
}

/* ─────────────────────────── Summary view ─────────────────────────── */

function RRSummaryView({ sites, summaryData, saliencyItems, onGoToRisk }) {
  const { rows, counts } = summaryData;

  return (
    <div className="rr-formwrap">
      <div className="rr-formhd">
        <div className="rr-formhd-id">
          <code className="rr-code">RR-RESUMEN</code>
          <span className="pill b">Vista consolidada</span>
          <span className="rr-formhd-title">Salida de Etapa 2.1 para alimentar Etapa 3</span>
        </div>
      </div>

      <div className="rr-hint rr-hint--info">
        <span className="rr-hint-ic">i</span>
        <div>
          Esta vista consolida los {rows.length} riesgos según OCDE 2.1.7. Los riesgos significantes alimentan el CAP Tracker.
          <div className="rr-hint-src">— Guía OCDE 2021, Sección 2.1.7</div>
        </div>
      </div>

      {/* KPIs */}
      <div className="rr-concl-grid">
        <div className="rr-concl-card crit"><div className="lbl">Críticos</div><div className="vl">{counts.crit}</div><div className="meta">significance ≥ 16</div></div>
        <div className="rr-concl-card high"><div className="lbl">Altos</div><div className="vl">{counts.high}</div><div className="meta">11–15</div></div>
        <div className="rr-concl-card med"><div className="lbl">Medios</div><div className="vl">{counts.med}</div><div className="meta">6–10</div></div>
        <div className="rr-concl-card low"><div className="lbl">Bajos</div><div className="vl">{counts.low}</div><div className="meta">1–5</div></div>
      </div>

      {/* Tabla resumen */}
      <div className="rr-fsec">
        <h4>Tabla consolidada · {rows.length} riesgos × {sites.length} {sites.length === 1 ? "sitio" : "sitios"}</h4>
        <div className="rr-pillar-sum">
          {Object.keys(PILLARS).map((k) => (
            <span key={k} className="rr-pillar" style={{ background: PILLARS[k].bg, color: PILLARS[k].fg }}>
              {PILLARS[k].label} · {rows.filter((d) => pillarOf(d.risk) === k).length}
            </span>
          ))}
        </div>
        <table className="rr-sum-table">
          <thead>
            <tr>
              <th>Riesgo OCDE</th>
              {sites.map((st) => <th key={st.id}>{st.flag} {st.name}</th>)}
              <th>Promedio</th><th>Nivel</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((d, ri) => {
              const cls = classify(d.avg);
              return (
                <tr key={d.risk.id} onClick={() => onGoToRisk(ri)} className="rr-sum-row">
                  <td>
                    <div className="rr-rname">{d.risk.name}</div>
                    <span className="rr-rref">{d.risk.section}</span> <PillarChip risk={d.risk} />
                  </td>
                  {d.sigs.map((s, si) => (
                    <td key={si} className="center">
                      <span className={`rr-sig ${classify(s)}`}>{s.toFixed(2)}</span>
                    </td>
                  ))}
                  <td className="center"><span className={`rr-sig ${cls}`}>{d.avg.toFixed(2)}</span></td>
                  <td className="center"><span className={`pill ${CLASS_PILL[cls]}`}>{CLASS_LABEL[cls]}</span></td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Saliency matrix */}
      <div className="rr-fsec">
        <h4>Saliency matrix · severidad × probabilidad (promedio grupo)</h4>
        <div className="rr-sal-outer">
          <div className="rr-sal-y">← Severidad</div>
          <div>
            <div className="rr-sal-grid">
              {[5,4,3,2,1].map((sev) => (
                <React.Fragment key={sev}>
                  <div className="rr-sal-row-hdr">{["Muy baja","Baja","Media","Alta","Muy alta"][sev-1]} ({sev})</div>
                  {[1,2,3,4,5].map((prob) => {
                    const sig = sev * prob;
                    const cls = classify(sig);
                    const dots = saliencyItems.filter((i) => i.roundSev === sev && i.roundProb === prob);
                    return (
                      <div key={prob} className={`rr-sal-cell ${cls}`}>
                        {dots.map((d) => (
                          <span key={d.mod} className="rr-sal-dot" onClick={() => onGoToRisk(d.idx)} title={d.name}>
                            {d.mod}
                          </span>
                        ))}
                      </div>
                    );
                  })}
                </React.Fragment>
              ))}
            </div>
            <div className="rr-sal-x-row">
              <div />
              {["Muy baja","Baja","Media","Alta","Muy alta"].map((lbl) => <div key={lbl} className="rr-sal-x">{lbl}</div>)}
            </div>
            <div className="rr-sal-x-caption">Probabilidad →</div>
          </div>
        </div>
      </div>
    </div>
  );
}
